<template>
    <div class="w-100">
        <button
            v-if="!isAddContact && !isEditContact"
            class="btn btn-kne-green w-100 mt-2 mb-2"
            :disabled="disabled"
            @click="openForm">
            {{ $t('OrderPending.addContacts.addContact') }}
            <i class="fa fa-solid fa-plus" />
        </button>
        <div v-else class="mt-2 contact-form px-3 py-2">
            <form @submit.prevent="addOrder">
                <regular-input
                    :value="formData.name"
                    @input="formData.name = $event"
                    type="text" :placeholder="$t('OrderPending.addContacts.name') + '*'" class="form-group"/>

                <div class="form-group">
                    <regular-input
                        :value="formData.email"
                        @input="updateEmail($event)"
                        type="email" 
                        :placeholder="$t('OrderPending.addContacts.email') + `*`" 
                    />
                    <div v-if="!isValidEmail && formData.email" class="error-message">
                        {{ $t('OrderPending.addContacts.invalidEmail') }}
                    </div>
                </div>
                    
                <regular-input
                    :value="formData.phone"
                    @input="updatePhone($event)"
                    type="tel"
                    :placeholder="$t('OrderPending.addContacts.phone') + `${formData.isContactSms ? '*' : ''} `" 
                    class="form-group"
                    maxlength="13"
                />
                
                <div class="toggle-button-group my-3">
                    <button
                        type="button"
                        v-for="option in dealerTypes"
                        :key="option.value"
                        :class="['toggle-button', { 'active': selectedOptions.includes(option.value) }]"
                        @click="toggleOption(option.value)">
                        {{ option.label }}
                    </button>
                </div>


                <regular-checkbox
                    :checked="formData.isContactSms"
                    @change="formData.isContactSms = $event"
                    class="form-group"
                    :label="$t('OrderPending.addContacts.sms_updates')"/>

                <regular-checkbox
                    :checked="formData.isSubscribedForUpdates"
                    @change="formData.isSubscribedForUpdates = $event"
                    class="form-group"
                    :label="$t('OrderPending.addContacts.email_updates')"/>

                <div>
                    <button type="submit" class="btn btn-kne-orange mb-2" :disabled="!formCompleted">
                        {{ isEditContact ? $t('OrderPending.addContacts.edit') : $t('OrderPending.addContacts.add') }}
                    </button>
                    <button type="button" class="btn btn-secondary mb-2" @click="resetForm">
                       {{ $t('OrderPending.addContacts.close') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import regularInput from './regularInput.vue'
import regularCheckbox from './regularCheckbox.vue'

export default {
    name: 'AddContact',

    components: {
        regularInput,
        regularCheckbox
    },

    props: {
        contactList: Array,
        isAddContact: Boolean,
        isEditContact: Boolean,
        editIndex: Number,
        disabled: {
            required: false,
            default: false,
            type: Boolean
        }
    },

    data () {
        return {
            formData: {
                email: '',
                dealerType: [],
                name: '',
                phone: '',
                isContactSms: false,
                isSubscribedForUpdates: true
            },
            dealerTypes: [
                {value: 'LOGISTICS', label: `${this.$t('OrderPending.addContacts.dealerTypes.logistics')}`},
                {value: 'BILLING', label: `${this.$t('OrderPending.addContacts.dealerTypes.billing')}`}
            ],
            isValidEmail: true
        }
    },

    watch: {
        isEditContact (newVal) {
            if (newVal) {
                const contact = this.contactList[this.editIndex]
                this.formData.name = contact.name
                this.formData.email = contact.email
                this.formData.dealerType = contact.dealerType
                this.formData.phone = contact.phone
                this.formData.isContactSms = contact.isContactSms
                if (contact.isSubscribedForUpdates === true || contact.isSubscribedForUpdates === false) {
                    this.formData.isSubscribedForUpdates = contact.isSubscribedForUpdates
                }
                this.selectedOptions = contact.dealerType
            } else {
                this.resetForm()
            }
        }},

    computed: {
        formCompleted () {
            return (
                this.validateEmail(this.formData.email) &&
                this.formData.dealerType.length !== 0 &&
                this.formData.name &&
                (!this.formData.isContactSms || this.formData.phone)
            )
        },
        selectedOptions: {
            get () {
                return this.formData.dealerType
            },
            set (value) {
                this.formData.dealerType = value
            }
        }
    },

    methods: {
        addOrder () {
            if (this.formCompleted) {
                if (this.isEditContact) {
                    this.contactList.splice(this.editIndex, 1, this.formData)
                    this.$emit('update:contactList', this.contactList)
                } else {
                    this.$emit('update:contactList', [...this.contactList, this.formData])
                }
                this.resetForm()
            }
        },

        toggleOption (value) {
            const index = this.selectedOptions.indexOf(value)
            if (index > -1) {
                this.selectedOptions = this.selectedOptions.filter(option => option !== value)
            } else {
                this.selectedOptions = [...this.selectedOptions, value]
            }
        },

        openForm () {
            this.resetForm()
            this.$emit('update:isAddContact', true)
        },

        resetForm () {
            this.formData = {
                email: '',
                dealerType: [],
                name: '',
                phone: '',
                isContactSms: false,
                isSubscribedForUpdates: true
            }
            this.selectedOptions = []
            this.$emit('update:isEditContact', false)
            this.$emit('update:isAddContact', false)
        },

        validateEmail (email) {
            const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            this.isValidEmail = emailPattern.test(email)
            return this.isValidEmail
        },

        updateEmail (value) {
            this.formData.email = value
            this.validateEmail(value)
        },

        updatePhone (event) {
            const value = event
            const numericValue = this.getNumericValue(value).slice(0, 13)
            this.formData.phone = numericValue
        },

        getNumericValue (value) {
            return value.replace(/\D/g, '')
        }
    }
}
</script>

<style scoped>
.toggle-button-group {
    display: flex;
}

.toggle-button {
    background: #d6d6d6;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
    padding: 10px;
    cursor: pointer;
    margin-right: 5px;
    outline: none;
}

.toggle-button.active {
    background-color: #007bff;
    color: white;
}

.contact-form {
    border: 1px solid #c2c2c2;
    border-radius: 0.25rem;
}

input[type="number"]::-webkit-inner-spin-button, 
input[type="number"]::-webkit-outer-spin-button { 
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] { 
  -moz-appearance: textfield;
}

.toggle-button:not(.active):hover {
    background-color: #f0f0f0;
}
.error-message {
    color: red;
    font-size: 0.8em;
    margin-top: 5px;
}
</style>
